import { Button, Tooltip } from '@gisce/oficina-virtual-components';
import {
  Card, CardContent, CardActions, 
  Typography, Box, CircularProgress, 
  Stack, Chip, Alert,
  Grid
} from '@mui/material';
import { 
  Feedback, GetApp, Visibility, 
  Receipt, AccountBalance, Power
} from '@mui/icons-material';

import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';
import useAuth from '@/components/Authentication/hooks/useAuth';

import TextFieldReadOnly from '@/components/TextFieldReadOnly';
import { useTranslation } from 'react-i18next';
import { ContractModificationTypes } from '../../types/contracts';
import { NoSIPSCessionAction } from './components/NoSIPSCessionAction';
import { useContracts } from '../../hooks/useContracts';
import CustomizeDoubleTariff from './components/CustomizeDoubleTariff';
import CustomizeHappyHours from './components/CustomizeHappyHours';
import CustomizeHappyDay from './components/CustomizeHappyDay';
import { FC } from 'react';

interface IContractDetailsPanelProps {
  openDialog: (type: ContractModificationTypes) => void;
}

export const GasContractDetailsPanel: FC<IContractDetailsPanelProps> = ({
  openDialog
}) => {

  const { t } = useTranslation();
  const { tokenVals } = useAuth();
  const { 
    goToFilteredInvoices, 
    fetchContracts, 
    currentContract: contract,
    downloadContract,
    downloadingContract
  } = useContracts('gas');
  const Settings = useSettingsContext();
  
  const tokenReadOnly = tokenVals?.readonly;

  return <>

    {(contract.state == "baixa" || contract.state == "cancelada") &&
      <Alert severity="warning" sx={{mb: 2}}>
        {t("common:text.contract_disabled_cant_modify")}
      </Alert>
    }

    <Card sx={{marginBottom: 3}}>
      <CardContent>
        <Stack direction="row" pb={2} gap={2}>
          <Typography variant="h5">
            {t('common:text.contract_data_contract')}
          </Typography>
        </Stack>
        {Settings.features?.noCesionSips && <div>
          <NoSIPSCessionAction
            noSIPSCession={contract.no_cession_sips}
            isAtrAvailable={contract.atr_available}
            isContactActive={contract.state === 'activa'}
            dialogOpen={() => {openDialog("noSIPSCession")}}
          />
        </div>}
        <Grid container>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly
              label={t('common:text.contract_owner')}
            >
              {contract?.owner_partner.name}
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly
              label={t('common:text.contract_payer')}
            >
              {contract?.fiscal_partner?.name}
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly label={t('common:text.contract_contracted_pressure')}>
              { contract.pressio } {t("common:text.contract_detail_gas_pressure_unit", {count: contract.pressio})}
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly label={t('common:text.contract_contracted_flow_rate')}>
              { contract.caudal }
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <div>
              {/* Si no te nom comercial llista de preus {_.get(contract, 'pricelist.name', '')} */}
              <TextFieldReadOnly label={t('common:text.contract_contracted_product')}>
                {contract?.pricelist.nom_comercial ?
                  contract?.pricelist?.nom_comercial : contract?.pricelist?.name
                }
              </TextFieldReadOnly>

              {Settings.contract?.customizeDoubleTariff && <CustomizeDoubleTariff
                contractId={contract.name}
                onUpdate={()=> fetchContracts(false)}
              />}
              {Settings.contract?.customizeHappyHours && <CustomizeHappyHours
                contractId={contract.name}
                onUpdate={()=> fetchContracts(false)}
              />}
              {Settings.contract?.customizeHappyDay && <CustomizeHappyDay
                contractId={contract.name}
                onUpdate={()=> fetchContracts(false)}
              />}
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly 
              label={t('common:text.contract_payment_account')}
            >
              {contract?.bank_account ? 
                contract.bank_account.printable_iban 
                : t('common:text.contract_no_domiciled')}
              {Settings.contract?.ibanChange?.enabled && 
                !Settings?.contract?.horizontalButtons &&
                contract.state == 'activa' && 
                <Tooltip
                  title={tokenReadOnly &&
                    t("common:text.token_readonly_tooltip")
                  }
                  placement="right"
                  arrow
                  disableFocusListener={!tokenReadOnly}
                  disableHoverListener={!tokenReadOnly}
                  disableTouchListener={!tokenReadOnly}
                  slotProps={{
                    popper: {
                      modifiers: [{
                        name: 'offset',
                        options: {offset: [0, -5]}
                      }]
                    }
                  }}
                >
                  <span style={{ 'width': '100%' }}>
                    <Button
                      variant={'text'}
                      color={'primary'}
                      disabled={!!tokenReadOnly}
                      title={t('common:text.contract_account_modification')}
                      onClick={() => openDialog("iban")}
                    >
                      {contract.bank_account ?
                        t('common:text.contract_modification') 
                        : t('common:text.contract_enable_domiciliation')}
                    </Button>
                  </span>
                </Tooltip>
              }
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly
              label={t('common:text.contract_nif')}
            >
              {contract?.fiscal_partner.vat}
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly
              label={t('common:text.contract_fiscal_address')}
            >
              {contract?.fiscal_address?.full_address}
            </TextFieldReadOnly>
          </Grid>
          {Settings?.contract?.refCatastral?.enabled &&
            <Grid item xs={12} md={6}>
              <TextFieldReadOnly label={t('common:text.contract_ref_catastral')}>
                {contract.ref_catastral}

                {Settings?.contract?.refCatastral?.modify && 
                  contract.state == 'activa' && 
                <Button
                  variant={'text'}
                  color={'primary'}
                  onClick={() => openDialog("refCatastral")}
                >
                  {contract.ref_catastral !== "" ?
                    t('common:text.contract_modification')
                    :
                    t('common:text.contract_add_ref_catastral')
                  }
                </Button>
                }
              </TextFieldReadOnly>
            </Grid>
          }
        </Grid>
      </CardContent>
      <CardActions>
        <Box flexWrap="wrap" px={1} pb={1}>
          { !Settings.features?.disableDownloadContractReport &&
            <Button
              key={'download'}
              variant={'contained'}
              color={'primary'}
              onClick={() => downloadContract()}
              disabled={downloadingContract}
              style={{marginRight: 12, marginTop: 8}}
            >
              {(downloadingContract ? <CircularProgress size={25} style={{marginRight: 10}} /> :
                <GetApp style={{marginRight: 10}}/>)}
              {t('common:text.contract_download')}
            </Button>
          }
          <Button
            key={'invoices'}
            variant={'contained'}
            color={'primary'}
            onClick={() => goToFilteredInvoices(contract)}
            title={t('common:text.contract_related_invoices')}
            style={{marginRight: 12, marginTop: 8}}
          >
            {<Receipt style={{marginRight: 10}}/>}
            {t('common:text.contracts_view_invoices_button_button')}
          </Button>

          {Settings.features?.reclamATCContract &&
            <Button
              key={'reclamation'}
              variant={'contained'}
              color={'primary'}
              onClick={() => openDialog("reclam")}
              style={{marginRight: 12, marginTop: 8}}
            >
              {<Feedback style={{marginRight: 10}}/>}
              {t('common:text.contract_create_atc_reclamation')}
            </Button>
          }
          {Settings.features?.powerChange && 
            Settings?.contract?.horizontalButtons && 
            contract.state !== 'activa' &&
            <Tooltip
              title={tokenReadOnly &&
                t("common:text.token_readonly_tooltip")
              }
              placement="bottom"
              arrow
              disableFocusListener={!tokenReadOnly}
              disableHoverListener={!tokenReadOnly}
              disableTouchListener={!tokenReadOnly}
              slotProps={{
                popper: {
                  modifiers: [{
                    name: 'offset',
                    options: {offset: [0, 10]}
                  }]
                }
              }}
            >
              <span style={{ 'width': '100%' }}>
                <Button
                  key={'changePower'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={!contract.atr_available || !!tokenReadOnly}
                  title={t('common:text.contract_power_modification')}
                  onClick={() => openDialog("power")}
                  style={{marginRight: 12, marginTop: 8}}
                >
                  {<Power style={{marginRight: 10}}/>}
                  {t('common:text.contract_power_modification')}
                </Button>
              </span>
            </Tooltip>
          }
          {Settings.features?.noCesionSips && 
            Settings?.contract?.horizontalButtons && 
            contract.state !== 'activa' &&
            <Tooltip
              title={tokenReadOnly &&
                t("common:text.token_readonly_tooltip")
              }
              placement="bottom"
              arrow
              disableFocusListener={!tokenReadOnly}
              disableHoverListener={!tokenReadOnly}
              disableTouchListener={!tokenReadOnly}
              slotProps={{
                popper: {
                  modifiers: [{
                    name: 'offset',
                    options: {offset: [0, 10]}
                  }]
                }
              }}
            >
              <span style={{ 'width': '100%' }}>
                <Button
                  key={'sips'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={!contract.atr_available || contract.no_cession_sips.state === 'active' || !!tokenReadOnly}
                  title={t('common:text.sips_title')}
                  onClick={() => openDialog("noSIPSCession")}
                  style={{marginRight: 12, marginTop: 8}}
                >
                  {<Visibility style={{marginRight: 10}}/>}
                  {t('common:text.sips_title')}
                </Button>
              </span>
            </Tooltip>
          }
          { Settings.contract?.ibanChange?.enabled 
            && contract.bank_account
            && Settings?.contract?.horizontalButtons &&
            contract.state !== 'activa' &&
            <Tooltip
              title={tokenReadOnly &&
                t("common:text.token_readonly_tooltip")
              }
              placement="bottom"
              arrow
              disableFocusListener={!tokenReadOnly}
              disableHoverListener={!tokenReadOnly}
              disableTouchListener={!tokenReadOnly}
              slotProps={{
                popper: {
                  modifiers: [{
                    name: 'offset',
                    options: {offset: [0, 10]}
                  }]
                }
              }}
            >
              <span style={{ 'width': '100%' }}>
                <Button
                  key={'iban'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={!!tokenReadOnly}
                  title={ contract.bank_account ?
                    t('common:text.contract_account_modification') 
                    : t('common:text.contract_enable_domiciliation')
                  }
                  onClick={() => openDialog("iban")}
                  style={{marginRight: 12, marginTop: 8}}
                >
                  {<AccountBalance style={{marginRight: 10}}/>}
                  {contract.bank_account ?
                    t('common:text.contract_account_modification') 
                    : t('common:text.contract_enable_domiciliation')}
                </Button>
              </span>
            </Tooltip>
          }
        </Box>
      </CardActions>
    </Card>
  </>
}